import {
  useOrderChannelStatus,
  usePauseOrders,
  useUnpauseOrders,
} from '@/features/order-setup/hooks/useOrderSetup';
import { useRestaurantTimezone } from '@/stores/restaurant.store';
import { useT } from '@/i18n';
import type { OrderChannelStatus } from '@restaurant/api-client';
import { f7 } from 'framework7-react';

/**
 * Compact online-ordering health strip, pinned above the orders list. One cell
 * per channel showing the *effective* state right now - Open / Closed (by
 * hours) / Paused - plus when it next changes, so the operator can read whether
 * pickup and delivery are actually taking orders at a glance and tap to pause or
 * resume. Fine-grained config lives in Settings → Order Setup.
 */
const ORDER_TYPES: ('PICKUP' | 'DELIVERY')[] = ['PICKUP', 'DELIVERY'];

export default function OrderPauseCard() {
  const t = useT();
  const tz = useRestaurantTimezone();
  const { data: statuses } = useOrderChannelStatus();
  const pause = usePauseOrders();
  const unpause = useUnpauseOrders();
  const busy = pause.isPending || unpause.isPending;

  const toast = (text: string) => f7.toast.show({ text, closeTimeout: 2000, position: 'bottom' });

  const statusFor = (orderType: 'PICKUP' | 'DELIVERY'): OrderChannelStatus =>
    statuses?.find((s) => s.orderType === orderType) ?? {
      orderType,
      availability: 'CLOSED',
      nextChangeAt: null,
      nextChangeKind: null,
    };

  const time = (iso: string): string =>
    new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: tz });

  // Compact "next change" - just the time, prefixed with a short weekday only
  // when it isn't today (no filler verbs; the dot colour conveys direction).
  const when = (iso: string): string => {
    const key = (d: Date | string) => new Date(d).toLocaleDateString('en-CA', { timeZone: tz });
    const prefix =
      key(iso) === key(new Date())
        ? ''
        : `${new Date(iso).toLocaleDateString([], { weekday: 'short', timeZone: tz })} `;
    return `${prefix}${time(iso)}`;
  };

  // State word + terse tail, e.g. "Open · 22:00", "Closed · Tue 11:00".
  const label = (s: OrderChannelStatus): { word: string; tail: string | null } => {
    if (s.availability === 'PAUSED') {
      return {
        word: t.orders.channelStatus.paused,
        tail: s.nextChangeAt ? when(s.nextChangeAt) : t.orders.channelStatus.close,
      };
    }
    const word =
      s.availability === 'OPEN' ? t.orders.channelStatus.open : t.orders.channelStatus.closed;
    return { word, tail: s.nextChangeAt ? when(s.nextChangeAt) : null };
  };

  const cssState = (s: OrderChannelStatus): string =>
    s.availability === 'OPEN' ? 'is-live' : s.availability === 'PAUSED' ? 'is-paused' : 'is-closed';

  const openActions = (s: OrderChannelStatus, channelLabel: string) => {
    const orderType = s.orderType;
    const pauseFor = (mode: 'TIMED' | 'UNTIL_CLOSE', durationMinutes?: number) =>
      pause.mutate(
        { orderType, mode, durationMinutes },
        {
          onSuccess: () =>
            toast(
              mode === 'UNTIL_CLOSE'
                ? t.orders.pause.toast.pausedUntilClose(channelLabel)
                : t.orders.pause.toast.pausedFor(channelLabel, durationMinutes ?? 0),
            ),
        },
      );

    type ActionButton = { text: string; label?: boolean; color?: string; onClick?: () => void };
    const pauseGroup: ActionButton[] = [
      { label: true, text: t.orders.pause.action.label(channelLabel) },
      { text: t.orders.pause.action.pause30, onClick: () => pauseFor('TIMED', 30) },
      { text: t.orders.pause.action.pause1hour, onClick: () => pauseFor('TIMED', 60) },
      { text: t.orders.pause.action.pauseHours(2), onClick: () => pauseFor('TIMED', 120) },
      { text: t.orders.pause.action.pauseHours(3), onClick: () => pauseFor('TIMED', 180) },
      { text: t.orders.pause.action.pauseHours(4), onClick: () => pauseFor('TIMED', 240) },
      { text: t.orders.pause.action.pauseUntilClose, onClick: () => pauseFor('UNTIL_CLOSE') },
    ];
    if (s.availability === 'PAUSED') {
      pauseGroup.push({
        text: t.orders.pause.action.resumeNow,
        color: 'green',
        onClick: () =>
          unpause.mutate(
            { orderType },
            { onSuccess: () => toast(t.orders.pause.toast.resumed(channelLabel)) },
          ),
      });
    }

    f7.actions.create({ buttons: [[...pauseGroup, { text: t.cancel, color: 'red' }]] }).open();
  };

  return (
    <div className="ord-health">
      {ORDER_TYPES.map((orderType) => {
        const channelLabel = t.orders.type[orderType];
        const s = statusFor(orderType);
        const { word, tail } = label(s);
        return (
          <button
            key={orderType}
            type="button"
            className={`ord-health-cell ${cssState(s)}`}
            disabled={busy}
            onClick={() => openActions(s, channelLabel)}
          >
            <span className="ord-health-cell__top">
              <span className="ord-health-cell__label">{channelLabel}</span>
              <i className="f7-icons ord-health-cell__chevron">chevron_down</i>
            </span>
            <span className="ord-health-cell__status">
              <span className="ord-health-dot" />
              <span className="ord-health-cell__status-text">
                {tail ? `${word} · ${tail}` : word}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}
